// Case study content for each project. Mirrors the original site's bento structure.

import { HeartIcon } from "lucide-react";

export const caseStudies = {
  'crm-platform-redesign': {
    slug: 'crm-platform-redesign',
    no: '01',
    tags: ['Fintech', 'BFSI'],
    title: 'CRM Platform Redesign',
    subtitle: 'Enterprise CRM platform built for BFSI clients',
    color: '#e8d5b7',
    heroBg: 'linear-gradient(135deg, #f0d9b5 0%, #d4a373 100%)',
    intro: "BusinessNext is an enterprise CRM platform built primarily for BFSI clients. The product handles complex workflows, high-volume data, and configurable modules across sales, service, and operations. The challenge was to improve usability, consistency, and scalability while maintaining enterprise-level flexibility.",
    cta: { label: 'View Case Study →', href: '#' },
    meta: {
      Role: ['Product Designer'],
      Team: ['Product Managers', 'Developers', 'Business Stakeholders'],
      Timeline: ['Sept 2022 – Nov 2025'],
      Skills: ['CRM design', 'Enterprise UX', 'Design Systems'],
    },
    stats: [
      { number: '16+', label: 'CRM modules launched' },
      { number: '3M+', label: 'Enterprise users reached' },
      { number: '40%', label: 'Boost in task completion' },
      { number: '200+', label: 'Components unified' },
    ],
    sections: [
      // Section media — Cloudinary public IDs
      // Full width (stacked):
      //   images: ['portfolio/work/.../img-1', 'portfolio/work/.../img-2']
      // Side by side (one row, 2 columns on sm+):
      //   images: [
      //     'portfolio/work/.../full-width',
      //     ['portfolio/work/.../left', 'portfolio/work/.../right'],
      //     // or: { row: ['portfolio/work/.../left', 'portfolio/work/.../right'] },
      //   ],
      // Carousel (auto-advances every 3s; optional carouselInterval in ms):
      //   carousel: [
      //     { title: 'Slide title', desc: 'Description', image: 'portfolio/work/.../id' },
      //     { image: { src: 'portfolio/.../id', width: 800, height: 600 } },
      //   ],
      // Custom display size (CSS only — Cloudinary still fetches at default resolution):
      //   video: { src: 'portfolio/.../id', width: 480, height: 960 }
      //   image: { src: 'portfolio/.../id', width: 400, height: 700 }
      // video: 'portfolio/projects/crm-platform-redesign/overview',
      {
        video: "portfolio/work/crm-platform-redesign/V1_zjryq8",
      },
      {
        kind: 'Problem', label: '', heading: 'The Problem',
        body: "Users were overwhelmed by dense layouts and lacked the ability to personalize or prioritize key metrics.",
      },
      {
        kind: 'context', label: 'Understanding the Impact', heading: 'How was this impacting users?',
        body: 'I dived deep into usage stats, support tickets, and user feedback to uncover how the old design was hurting users including developers. The data showed clear productivity and efficiency hits.\n\nKey Issues:\n• No Uniformity: CRM screens were feature-heavy and visually dense\n• 4.6/10 Usability survey score\n• 3M+ Enterprise users with no similarity in experience\n• 56% support tickets related to users unable to analyze key insights',
      },
      {
        kind: 'solution', label: '', heading: 'Redesigning with Intent',
        body: 'I began by mapping the most frequent workflows and identifying friction points at each step. Focused on removing barriers, eliminating unnecessary clicks, surfacing essential data, and streamlining how users moved through lead and pipeline views.',
        carousel: [
          {
            image: "portfolio/work/crm-platform-redesign/1_bn6idm",
            title: 'Persona 1',
          },
          {
            image: "portfolio/work/crm-platform-redesign/2_fddwk7",
            title: 'Persona 2',
          },
          {
            image: "portfolio/work/crm-platform-redesign/4_zdb93u",
            title: 'Journey Map',
          },
          {
            image: "portfolio/work/crm-platform-redesign/5_ipfdf8",
            title: 'Ecosystem',
          },
        ],
        image: "portfolio/work/crm-platform-redesign/3_svyiy2",
      },
      {
        kind: 'process', label: 'Design Process', heading: 'Our Approach',
        body: 'Problem understanding, goal definition, diverse idea generation, informed decision-making, concept prototyping, and user validation through testing. I took an iterative approach for user-centered solutions, addressing challenges and aligning with business objectives.',
        carousel: [
          {
            image: "portfolio/work/crm-platform-redesign/6_tjye2r",
            title: 'Design Process',
          },
          {
            image: "portfolio/work/crm-platform-redesign/7_c9rifi",
            title: 'Empathy Mapping',
          },
          {
            image: "portfolio/work/crm-platform-redesign/8_nsn73t",
            title: 'User Mapping',
          },
          {
            image: "portfolio/work/crm-platform-redesign/9_ikxiiu",
            title: 'User Mapping',
          },
        ],
      },
      {
        kind: 'features', label: 'Key Design Solutions', heading: 'Solutions & Impact',
        features: [
          {
            title: 'Summary Page — Clear & Insightful',
            desc:
              'Problems: Overloaded screens, low clarity, slow scanning. Solutions: Card-based design, grouped insights, action visibility, clear navigation patterns. Impact: Faster scanning and improved focus',
            video: 'portfolio/work/crm-platform-redesign/Summary_Page_Clear_Insightful_eceses',
          },
          {
            title: 'Object Page — Personalized & Actionable',
            desc:
              'Problems: Hard to prioritize leads/customers, poor structure. Solutions: Insight dashlets, personalized modes, Kanban/list/grid/split views, drawer. Impact: Faster updates and better lead prioritization',
            video: 'portfolio/work/crm-platform-redesign/Object_page_Personalized_Actionable_xbpshj',
          },
          {
            title: '360 View — Full Context for Decisions',
            desc:
              'Problems: Fragmented data, no holistic view. Solutions: Unified quick + detailed views, predictive insights, customer timeline. Impact: Improved confidence and faster decisions',
            video: "portfolio/work/crm-platform-redesign/360_Full_Context_for_Decisions_fcdq2x",
          },
          {
            title: 'Form Filling — Guided & Structured',
            desc:
              'Problems: User fatigue, errors due to complexity. Solutions: Progress bar, step guidance, notes for context, step previews. Impact: Higher completion accuracy and speed',
            video: "portfolio/work/crm-platform-redesign/Form_Filling_Guided_Structured_n92eil",
          },
          {
            title: 'Split View — Smooth Multitasking',
            desc:
              'Problems: Constant switching, breaks in flow. Solution: Dual-pane layouts. Impact: Reduced clicks and smoother task handling',
            video: "portfolio/work/crm-platform-redesign/Split_View_Smooth_Multitasking_vud5nn",
          },
          {
            title: 'Drawer — Continuous Workflow',
            desc:
              'Problems: Pop-ups breaking user flow. Solution: Side drawer for contextual details. Impact: Clean, uninterrupted screen experience',
            video: "portfolio/work/crm-platform-redesign/Drawer_Continuous_Workflow_kixnee",
          },
        ],
      },
      {
        kind: 'launch', label: 'Launch Strategy', heading: 'Phased Rollout',
        body: 'The CRM redesign was rolled out in phased releases to reduce risk and ensure operational continuity for relationship managers.\n\n• Started with high-impact workflows (Summary Dashboard, Lead Creation, Customer 360, Task Management)\n• Conducted internal UAT sessions with QA & dev team\n• AB testing for modules\n• Gathered structured feedback through walkthrough sessions and usage analytics\n• Iterated quickly before full-scale rollout',
      },
      {
        kind: 'Results', label: 'Results', heading: 'Post-Redesign Impact',
        body: "Reduced task completion time\n• Improved form accuracy\n• Decreased user errors\n• Increased feature adoption\n• Positive client feedback\n\nWe also reduced the design-engineering gap significantly.",
      },
      {
        kind: 'metrics', label: 'Impact & Results', heading: 'Measurable Outcomes',
        body: 'Efficiency Gains: Reduced time to create a lead through progressive disclosure, reduced excessive scrolling and navigation steps, fewer clicks through grouped data.\n\nReduced Iterations: 80% reduction in post-development rework due to early stakeholder alignment and prototype validation.\n\nImproved Data Accuracy: Structured form grouping improved required field completion, fewer manual corrections by operations teams.\n\nFeature Absorption: Higher daily usage of CRM workflows, reduced fallback to manual tracking methods, positive RM feedback on clarity and usability.\n\nSystem Scalability: Introduced reusable workflow patterns and modular components, improved design system maturity for future modules.',
      },
      {
        kind: 'learnings', label: 'Key Learnings', heading: 'What I Learned',
        reflections: [
          { title: 'Enterprise balance', desc: 'Enterprise products require balance between flexibility and simplicity' },
          { title: 'Engineering collaboration', desc: 'Early engineering collaboration reduces rework' },
          { title: 'Data hierarchy', desc: 'Clear data hierarchy is critical in CRM systems' },
          { title: 'Consistency builds trust', desc: 'Consistency builds confidence in enterprise tools' },
        ],
      },
      {
        kind: 'next', label: 'Next Steps', heading: 'What\'s Next',
        body: 'Creation of Swift 2.0 — a refined and more structured version of critical modules.',
      },
    ],
  },
  'ai-document-extraction-for-crm': {
    slug: 'ai-document-extraction-for-crm',
    no: '02',
    tags: ['Product Design', 'Case Study'],
    title: 'AI Document Extraction for CRM',
    subtitle: 'AI-powered document processing and data extraction for enterprise CRM workflows',
    color: '#e8f0fe',
    heroBg: 'linear-gradient(135deg, #eef4ff 0%, #c8d8ff 100%)',

    intro:
      'Designed an AI-powered document extraction experience that enables businesses to automatically process documents, extract key information, and populate CRM records with minimal manual effort.',

    meta: {
      Role: ['Product Designer'],
      Team: ['Product Managers', 'Developers', 'Business Stakeholders'],
      Timeline: ['Oct - Nov 2025'],
      Skills: [
        'UX Research',
        'Product Design',
        'Information Architecture',
        'Interaction Design',
        'Design Systems',
        'Prototyping'
      ],
    },

    sections: [
      {
        kind: 'context',
        label: 'Overview',
        heading: 'Simplifying document extraction for CRM users.',
        body:
          'Businesses receive large volumes of customer documents that need to be manually reviewed and entered into CRM systems. This process is repetitive, error-prone, and time-consuming. The goal was to create an AI-driven workflow that automates extraction and reduces operational effort.',
      },

      {
        kind: 'context',
        label: 'Problem Statement',
        heading: 'Manual document processing slows down business operations.',
        body:
          'Users had to manually upload, inspect, validate, and transfer data from documents into CRM records. Existing workflows required significant effort and often resulted in inconsistent data entry.'
      },

      {
        kind: 'callout',
        heading:
          'How might we leverage AI to automatically extract, validate, and populate CRM data while keeping users in control?'
      },

      {
        kind: 'features',
        label: 'User Stories',
        heading: 'Key user needs identified during discovery',
        features: [
          {
            title: 'Training AI Models',
            desc:
              'As a CRM user, I want to train AI using business documents so that extraction accuracy improves over time.'
          },
          {
            title: 'Configure Extraction',
            desc:
              'As a system administrator, I want to configure document fields and mappings so extracted data aligns with CRM entities.'
          },
          {
            title: 'Review & Validate',
            desc:
              'As an end user, I want to review extracted information before submission to ensure accuracy and compliance.'
          }
        ],
      },

      {
        kind: 'context',
        label: 'Research',
        heading: 'Validating assumptions through hypothesis mapping.',
        body:
          'A hypothesis canvas was created to identify user pain points, business opportunities, success metrics, and assumptions. This helped prioritize features and align stakeholders on the product direction.',
        image: "portfolio/work/ai-document-extraction-for-crm/2_zb0vo3"  
      },

      {
        kind: 'context',
        label: 'Persona',
        heading: 'Understanding the primary enterprise user.',
        body:
          'Research identified CRM administrators and operations teams as the primary users. Their goals centered around reducing manual work, improving data accuracy, and accelerating customer onboarding processes.',
        image: "portfolio/work/ai-document-extraction-for-crm/3_llb3sn"  
      },

      {
        kind: 'context',
        label: 'Information Architecture',
        heading: 'Structuring a scalable document-processing workflow.',
        body:
          'Information architecture exercises defined the complete flow—from document upload and AI processing to validation, correction, and final CRM integration.',
        features: [
          {
            title: 'Training Journey',
            desc:
              'Users can upload sample documents and train AI models to recognize and extract relevant information.',
            image: "portfolio/work/ai-document-extraction-for-crm/4_iqzcs4"
          },
          {
            title: 'Configuration Journey',
            desc:
              'Administrators can define extraction rules, field mappings, and document templates to customize the AI pipeline.',
            image: "portfolio/work/ai-document-extraction-for-crm/5_kx4von"
          },
          {
            title: 'Consumption Journey',
            desc:
              'Users can review AI-generated outputs, correct inaccuracies, and continuously improve extraction quality.',
            image: "portfolio/work/ai-document-extraction-for-crm/6_aag9jy"
          }
        ]
      },

      {
        kind: 'features',
        label: 'Ideation',
        heading: 'Design explorations focused on three major workflows',
        body: "I started by sketching the user flows on paper to quickly explore how each journey would work. These sketches were then converted into mockups and prototypes. Through multiple iterations, the designs were reviewed and validated with stakeholders, engineering, and end users through usability testing.",
        carousel: [
          {
            title: 'Level 1',
            image: 'portfolio/work/ai-document-extraction-for-crm/7_rc7vz9',
          },
          {
            title: 'Level 2',
            image: 'portfolio/work/ai-document-extraction-for-crm/8_wouhjx',
          },
          {
            title: 'Level 3',
            image: 'portfolio/work/ai-document-extraction-for-crm/9_tq3cmx',
          },
        ],
      },

      {
        kind: 'features',
        label: 'Solution',
        heading: 'The final experience enables users to…',
        features: [
          {
            title: 'Upload and process documents',
            desc:
              'Users can upload multiple document types and initiate AI-powered extraction workflows.',
            video: "portfolio/work/ai-document-extraction-for-crm/V1_x5weui"    
          },
          {
            title: 'Automatically extract information',
            desc:
              'AI identifies important fields and populates CRM records with structured information.',
            video: "portfolio/work/ai-document-extraction-for-crm/V2_ktri0s"  
          },
          {
            title: 'Validate and correct extracted data',
            desc:
              'Users can review extracted values, make corrections, and approve final outputs before submission.',
            video: "portfolio/work/ai-document-extraction-for-crm/V3_tqf0z7"  
          }
        ]
      },

      {
        kind: 'reflection',
        label: 'Results & Impact',
        heading: 'Delivering a faster and more efficient CRM workflow.',
        body:
          'The solution significantly reduced manual effort in document processing, improved data accuracy, and streamlined CRM onboarding workflows through AI-assisted automation.',
        reflections: [
          {
            title: 'Reduced manual effort',
            desc:
              'Automated extraction minimized repetitive data-entry tasks.'
          },
          {
            title: 'Improved accuracy',
            desc:
              'Human-in-the-loop validation ensured reliable and trustworthy results.'
          },
          {
            title: 'Scalable architecture',
            desc:
              'The workflow supports future document types and enterprise use cases.'
          }
        ]
      }
    ]
  },
  //   subtitle: 'Interactive year-in-review for college students',
  //   color: '#1a2a5e', heroBg: 'linear-gradient(135deg, #1a2a5e 0%, #2c4288 100%)', heroDark: true,
  //   intro: 'A fun-filled, interactive lookback into college semesters built for students. 10K+ users across 200 universities and 90 countries \u2014 launched in just 6 weeks.',
  //   meta: {
  //     Role: ['Co-Creator', 'Design Engineer'],
  //     Team: ['Simon Ilincev'],
  //     Timeline: ['Apr \u2013 May 2025'],
  //     Skills: ['Web Design', 'Motion Design', 'Marketing'],
  //   },
  //   sections: [
  //     { kind: 'context', label: 'The idea', heading: 'A Spotify Wrapped, but for your calendar.',
  //       body: 'College students live by their calendars. We wanted to turn that data into a delightful, shareable reflection \u2014 a moment of joy at the end of a draining semester.' },
  //     { kind: 'features', label: 'Highlights', heading: 'What we built',
  //       features: [
  //         { title: '10K+ users', desc: 'Spread across 200+ universities and 90+ countries within two weeks of launch.' },
  //         { title: 'Custom illustrations', desc: 'Hand-crafted scenes for each section to keep things personal.' },
  //         { title: 'Sharable cards', desc: 'Designed for screenshots and Instagram stories.' },
  //       ] },
  //     { kind: 'reflection', label: 'Reflection', heading: 'Shipping fast matters',
  //       body: 'We shipped in 6 weeks because we trusted our instincts and kept scope tight. The lesson: momentum beats perfection.' },
  //   ],
  // },
  'agentic-dashboard': {
    slug: 'agentic-dashboard',
    no: '03',
    tags: ['Enterprise Product', 'AI', 'Shipped'],

    title: 'Agentic Dashboard',
    subtitle: 'Building contextual AI assistants that seamlessly integrate into enterprise CRM workflows',

    color: '#dce8ff',
    heroBg: 'linear-gradient(135deg, #dce8ff 0%, #8fb4ff 100%)',

    intro:
      'Designed an enterprise AI experience that acts as a trusted assistant within CRM workflows, helping relationship managers automate tasks, generate insights, and engage customers more effectively while maintaining user trust and control.',

    meta: {
      Role: ['Product Designer'],
      Team: ['Product Managers', 'Engineers', 'Business Stakeholders'],
      Timeline: ['Apr - May 2026'],
      Skills: [
        'Product Design',
        'AI Experience Design',
        'UX Strategy',
        'Interaction Design',
        'Enterprise UX',
        'Design Systems',
      ],
    },

    sections: [
      {
        kind: 'context',
        label: 'Overview',
        heading: 'Designing AI that users can trust and rely on.',
        body:
          'As AI capabilities rapidly evolve, enterprise users often hesitate to adopt AI due to concerns around transparency, reliability, and loss of control. The challenge was to design AI experiences that augment human decision-making rather than replace it.',
        video: "portfolio/work/agentic-dashboard/V1_yevegf"  
      },

      {
        kind: 'context',
        label: 'Product Strategy',
        heading: 'Creating an AI assistant embedded directly into CRM workflows.',
        body:
          'The goal was to build contextual AI experiences that help relationship managers complete tasks faster, surface actionable insights, and automate repetitive work without disrupting their existing workflows.',
      },

      {
        kind: 'callout',
        heading:
          'How might we design enterprise AI that feels like a trusted teammate instead of an unpredictable black box?'
      },

      {
        kind: 'context',
        label: 'Key Principle',
        heading: "AI shouldn't replace users — it should amplify them.",
        body:
          'The experience was built around human-in-the-loop interactions where users always retain visibility, control, and final decision-making authority.'
      },

      {
        kind: 'features',
        label: 'Training AI',
        heading: 'Training AI the right way',
        features: [
          {
            title: 'Context-aware learning',
            desc:
              'AI models are trained using business-specific data and workflows to ensure domain relevance and higher accuracy.'
          },
          {
            title: 'Human validation',
            desc:
              'Users review, refine, and validate AI-generated outputs before actions are finalized.'
          },
          {
            title: 'Continuous improvement',
            desc:
              'Feedback loops help improve recommendations and system intelligence over time.'
          }
        ],
        carousel: [
          {
            image: 'portfolio/work/agentic-dashboard/2_ybcfqi',
          },
          {
            image: 'portfolio/work/agentic-dashboard/2_ybcfqi',
          },
        ]
      },

      {
        kind: 'context',
        label: 'Preview Before Trust',
        heading: 'Give users confidence before they commit.',
        body:
          'Rather than automatically executing actions, AI-generated outputs are presented as previews so users can review suggestions, verify information, and make informed decisions.',
        image: "portfolio/work/agentic-dashboard/3_ne4oon"  
      },

      {
        kind: 'features',
        label: 'Use Cases',
        heading: 'AI integrated across multiple CRM workflows',
        features: [
          {
            title: 'Relationship Insights',
            desc:
              'Surface meaningful customer insights, identify opportunities, and help relationship managers prioritize actions.',
            video: "portfolio/work/agentic-dashboard/V2_vrd03y"  
          },
          {
            title: 'Account Summaries',
            desc:
              'Generate contextual summaries by aggregating customer interactions, activities, and engagement history.',
            video: "portfolio/work/agentic-dashboard/V3_tg9dlc"  
          },
          {
            title: 'Smart Outreach',
            desc:
              'Assist users in drafting emails, WhatsApp messages, and personalized communications directly from the CRM.',
            video: "portfolio/work/agentic-dashboard/V5_ogyxv9"  
          }
        ]
      },

      {
        kind: 'features',
        label: 'Solution',
        heading: 'The AI assistant enables users to…',
        features: [
          {
            title: 'Discover actionable insights',
            desc:
              'AI analyzes customer data and highlights trends, risks, and opportunities requiring attention.'
          },
          {
            title: 'Reduce repetitive tasks',
            desc:
              'Routine activities such as summarization, data gathering, and communication drafting are automated.'
          },
          {
            title: 'Work faster without losing control',
            desc:
              'Every recommendation is transparent, editable, and requires user approval before execution.'
          }
        ]
      },

      {
        kind: 'reflection',
        label: 'Results & Impact',
        heading: 'Building trust is the foundation of successful enterprise AI.',
        body:
          'The final experience introduced AI capabilities into critical CRM workflows while preserving transparency, explainability, and user control — leading to higher confidence and adoption among enterprise users.',
        reflections: [
          {
            title: 'Trust drives adoption',
            desc:
              'Enterprise users are more likely to embrace AI when recommendations are transparent and reviewable.'
          },
          {
            title: 'Human-in-the-loop matters',
            desc:
              'Keeping users involved in decision-making significantly improves confidence in AI systems.'
          },
          {
            title: 'Context is everything',
            desc:
              'AI becomes more valuable when deeply embedded into existing workflows and business processes.'
          }
        ]
      }
    ]
  },
  'route-planner-mobile-app': {
    slug: 'route-planner-mobile-app',
    no: '04',
    tags: ['Enterprise Product', 'Mobile App', 'Shipped'],

    title: 'Route Planner (Mobile App)',
    subtitle: 'Helping relationship managers efficiently plan visits and manage field activities',

    color: '#e7eefb',
    heroBg: 'linear-gradient(135deg, #eef4ff 0%, #b8cbff 100%)',

    intro:
      'Designed a native mobile route-planning experience for relationship managers, enabling them to efficiently schedule visits, optimize travel, and improve productivity while working in the field.',

    meta: {
      Role: ['Product Designer'],
      Team: ['Product Managers', 'Engineers', 'Business Stakeholders'],
      Timeline: ['Dec 2024 - Nov 2025'],
      Skills: [
        'Mobile UX',
        'User Research',
        'Journey Mapping',
        'Information Architecture',
        'Interaction Design',
        'Enterprise UX'
      ],
    },

    sections: [
      {
        kind: 'context',
        label: 'Overview',
        heading: 'Optimizing field operations for relationship managers.',
        body:
          'Relationship managers spend a significant amount of time traveling between customer meetings. Existing planning methods were fragmented, leading to inefficient schedules, excessive travel, and reduced customer engagement.',
        video: { src: "portfolio/work/route-planner-mobile-app/V1_cp27uz", height: 480 }
      },

      {
        kind: 'context',
        label: 'Product Strategy',
        heading: 'Creating a smarter way to plan customer visits.',
        body:
          'The objective was to build a mobile-first route planner that allows relationship managers to organize visits, prioritize accounts, and optimize travel schedules directly from their smartphones.'
      },

      {
        kind: 'callout',
        heading:
          'How might we help relationship managers spend less time planning and more time engaging with customers?'
      },

      {
        kind: 'context',
        label: 'Persona Mapping',
        heading: 'Understanding the needs of relationship managers.',
        body:
          'Research revealed that relationship managers frequently work on the move and require quick access to schedules, customer information, and visit planning tools while in the field.',
        image: { src: "portfolio/work/route-planner-mobile-app/1_ihbaio", width: 500, height: 600 }  
      },

      {
        kind: 'context',
        label: 'Journey Mapping',
        heading: 'Mapping the complete field-work journey.',
        body:
          'User interviews and journey mapping exercises uncovered pain points across visit planning, travel management, customer prioritization, and post-visit follow-up activities.',
        carousel: [
          {
            image: "portfolio/work/route-planner-mobile-app/2_ugbudp",
          },
          {
            image: "portfolio/work/route-planner-mobile-app/3_trjv1m",
          },
        ]
      },

      {
        kind: 'features',
        label: 'Research Insights',
        heading: 'Key learnings from competitive benchmarking',
        features: [
          {
            title: 'Simplified planning flows',
            desc:
              'Users preferred lightweight and intuitive planning experiences with minimal steps.'
          },
          {
            title: 'Contextual customer information',
            desc:
              'Relationship managers needed quick access to account details during planning.'
          },
          {
            title: 'Mobile-first experience',
            desc:
              'Field teams primarily relied on mobile devices while traveling.'
          }
        ],
        image: { src: "portfolio/work/route-planner-mobile-app/4_yiq2db", width: 560, height: 580 }
      },

      {
        kind: 'context',
        label: 'Design Validation',
        heading: 'Validating concepts before implementation.',
        body:
          'Design workshops and validation sessions with stakeholders ensured that proposed workflows aligned with business goals while addressing real user challenges.'
      },

      {
        kind: 'context',
        label: 'Information Architecture',
        heading: 'Structuring a scalable route-planning experience.',
        body:
          'The information architecture focused on enabling users to seamlessly discover customers, create visit plans, optimize travel routes, manage schedules, and track field activities from a single workflow.',
        image: { src: "portfolio/work/route-planner-mobile-app/5_jjew0w", width: 650, height: 580 }  
      },

      {
        kind: 'features',
        label: 'Solution',
        heading: 'The mobile experience enables users to…',
        features: [
          {
            title: 'Plan visits efficiently',
            desc:
              'Relationship managers can schedule and organize customer visits with minimal effort.'
          },
          {
            title: 'Optimize travel routes',
            desc:
              'Smart route recommendations help reduce travel time and improve productivity.'
          },
          {
            title: 'Prioritize high-value accounts',
            desc:
              'Users can quickly identify and focus on important customer engagements.'
          }
        ],
        carousel: [
          {
            image: "portfolio/work/route-planner-mobile-app/6_oiamff",
          },
          {
            image: "portfolio/work/route-planner-mobile-app/7_fes5mi",
          },
          {
            image: "portfolio/work/route-planner-mobile-app/8_f4a63a",
          },
          {
            image: "portfolio/work/route-planner-mobile-app/9_azqb38",
          },
          {
            image: "portfolio/work/route-planner-mobile-app/10_qzsakn",
          },
          {
            image: "portfolio/work/route-planner-mobile-app/11_eodbep",
          },
        ]
      },

      {
        kind: 'reflection',
        label: 'Results & Impact',
        heading: 'Driving efficiency for field sales teams.',
        body:
          'The final solution streamlined route planning and improved productivity for relationship managers by reducing planning overhead and enabling more meaningful customer interactions.',
        reflections: [
          {
            title: 'Reduced planning effort',
            desc:
              'Users could create and manage visit schedules much faster than before.'
          },
          {
            title: 'Improved field productivity',
            desc:
              'Optimized routes allowed relationship managers to meet more customers in less time.'
          },
          {
            title: 'Better customer engagement',
            desc:
              'More efficient planning enabled teams to spend additional time building customer relationships.'
          }
        ]
      }
    ]
  },
  'crm-design-system': {
    slug: 'crm-design-system',
    no: '05',
    tags: ['Design System', 'Enterprise', 'Shipped'],

    title: 'CRM Design System',
    subtitle: 'Building a scalable enterprise design system for consistency and faster product delivery',

    color: '#e8ecff',
    heroBg: 'linear-gradient(135deg, #eef2ff 0%, #c7d2fe 100%)',

    intro:
      'Designed and established a comprehensive atomic design system that unified product experiences across enterprise applications, accelerated design-to-development workflows, and improved consistency at scale.',

    meta: {
      Role: ['Product Designer'],
      Team: ['Design Team', 'Frontend Engineers', 'Product Managers'],
      Timeline: ['Sept 2023 - Nov 2025'],
      Skills: [
        'Design Systems',
        'Token Architecture',
        'Figma',
        'Component Design',
        'Documentation',
        'Enterprise UX'
      ],
    },

    sections: [
      {
        kind: 'context',
        label: 'Overview',
        heading: 'Creating a scalable foundation for enterprise products.',
        body:
          'As multiple products evolved independently, inconsistencies began to emerge across UI patterns, components, spacing, and visual language. The goal was to establish a unified design system that ensured consistency while improving collaboration between designers and developers.',
        image: "portfolio/work/crm-design-system/1_vlkkqi"
      },

      {
        kind: 'context',
        label: 'Problem Statement',
        heading: 'Fragmented design patterns slowed product development.',
        body:
          'Different teams were creating and maintaining their own UI solutions, resulting in duplicated effort, inconsistent experiences, and increased design and development overhead.'
      },

      {
        kind: 'callout',
        heading:
          'How might we create a scalable design language that enables teams to build faster while maintaining consistency across products?'
      },

      {
        kind: 'context',
        label: 'Design Strategy',
        heading: 'Establishing a robust token architecture.',
        body:
          'The design system was structured around atomic design principles and a token-driven architecture, enabling scalability, flexibility, and easier maintenance across multiple products.',
        image: "portfolio/work/crm-design-system/2_ju4hig"
      },

      {
        kind: 'features',
        label: 'Foundation',
        heading: 'Core foundations that power the entire system',
        features: [
          {
            title: 'Design Tokens',
            desc:
              'Established a token architecture covering color, typography, spacing, elevation, radius, and motion.'
          },
          {
            title: 'Semantic Variables',
            desc:
              'Created semantic tokens that allowed themes and product customization without affecting components.'
          },
          {
            title: 'Scalable Architecture',
            desc:
              'Built the system to support future products and evolving business requirements.'
          }
        ],
        carousel: [
          {
            image: "portfolio/work/crm-design-system/3_eizcfm",
          },
          {
            image: "portfolio/work/crm-design-system/4_vatwmw",
          }
        ]
      },

      {
        kind: 'features',
        label: 'Atoms',
        heading: 'Creating reusable foundational components',
        features: [
          {
            title: 'Buttons, Inputs & Controls',
            desc:
              'Standardized the smallest UI building blocks with clearly defined states and behaviors.'
          },
          {
            title: 'Typography System',
            desc:
              'Defined consistent text styles, hierarchy, and accessibility standards.'
          },
          {
            title: 'Icons & Visual Elements',
            desc:
              'Unified iconography and supporting visual patterns across the ecosystem.'
          }
        ],
        image: "portfolio/work/crm-design-system/5_lst0qs"
      },

      {
        kind: 'features',
        label: 'Molecules',
        heading: 'Combining atoms into meaningful interactions',
        features: [
          {
            title: 'Forms & Field Groups',
            desc:
              'Created reusable form patterns that ensured consistency across complex enterprise workflows.'
          },
          {
            title: 'Cards & Information Blocks',
            desc:
              'Designed flexible information containers adaptable to multiple use cases.'
          },
          {
            title: 'Navigation Patterns',
            desc:
              'Standardized navigation behaviors and interaction patterns across products.'
          }
        ],
        image: "portfolio/work/crm-design-system/6_htkrm9"
      },

      {
        kind: 'features',
        label: 'Organisms',
        heading: 'Building large-scale reusable experiences',
        features: [
          {
            title: 'Dashboard Modules',
            desc:
              'Developed reusable dashboard structures and business widgets for enterprise applications.'
          },
          {
            title: 'Data-heavy Interfaces',
            desc:
              'Standardized tables, filters, panels, and complex information layouts.'
          },
          {
            title: 'Workflow Components',
            desc:
              'Created composable patterns for enterprise task flows and process management.'
          }
        ],
        image: "portfolio/work/crm-design-system/7_iqqjh4"
      },

      {
        kind: 'context',
        label: 'Templates',
        heading: 'Accelerating product creation with reusable layouts.',
        body:
          'Template-level structures provided teams with pre-defined layouts and interaction patterns, enabling faster product development while ensuring design consistency.'
      },

      {
        kind: 'reflection',
        label: 'Results',
        heading: 'Driving consistency and efficiency across teams.',
        body:
          'The design system significantly improved collaboration between design and engineering teams, reduced duplication, and accelerated product delivery across enterprise applications.',
        reflections: [
          {
            title: 'Faster delivery',
            desc:
              'Reusable components and templates reduced design and development effort.'
          },
          {
            title: 'Improved consistency',
            desc:
              'Products across the ecosystem now follow a unified visual and interaction language.'
          },
          {
            title: 'Better collaboration',
            desc:
              'Shared documentation and standardized patterns strengthened design-engineering alignment.'
          }
        ]
      },

      {
        kind: 'reflection',
        label: 'Key Learnings',
        heading: 'Design systems are products, not libraries.',
        body:
          'A successful design system requires continuous iteration, documentation, stakeholder buy-in, and close collaboration between design and engineering teams.',
        reflections: [
          {
            title: 'Start with foundations',
            desc:
              'Strong tokens and semantic layers make systems scalable and maintainable.'
          },
          {
            title: 'Document everything',
            desc:
              'Clear documentation is essential for adoption and long-term success.'
          },
          {
            title: 'Collaborate continuously',
            desc:
              'Design systems thrive when designers and developers build together.'
          }
        ]
      }
    ]
  }
};

export function listSlugs() {
  return Object.keys(caseStudies);
}
